import moment, { Moment } from '@e3dc-react/shell/Libs/moment'
import React, { useMemo, useState } from 'react'
import { RechartData, ChartView, CustomLayout, RechartDataObject, RechartSum, TraceSuffix } from 'src/Libs/chart-kit/shared/utils/chartModel'
import { traceConf, TraceKey } from 'src/Libs/system-charts/shared/config/traceConf'
import { useFetchData } from 'src/Libs/system-data-handler/api/SystemDataFetching'
import { getRoundedNumber } from 'src/Libs/system-data-handler/utils/helperFunctions'
import SystemChart from './SystemChart'
import { getComparisonYears } from '../utils/SystemChartUtils'
import { ChartSettings } from '../../../chart-kit/editor'

interface Props {
  storageId?: string
  rechartData: RechartData
  view: ChartView
  customLayout?: CustomLayout
  handleSaveChart?: (props: ChartSettings) => void
  handleDeleteChart?: (id: number | string) => void
  from: Moment
  to: Moment
  maxHeight?: number
  pieChartTooltipText?: string
  isDynamicPowerPriceEnabled: boolean /* this is a temporary condition added with AF-3237. The plan is to remove it after SW1 and SW2 align. */
  toggleHideLegend: () => void
}

const getSums = (
  data: Record<string, number | string | boolean>[],
  factor: number,
  suffixes: TraceSuffix[],
  customLayout?: CustomLayout
): Record<string, RechartSum> => {
  const sumKeys = data[0] ? Object.keys(data[0]) : []
  const sum: Record<string, RechartSum> = {}

  sumKeys.forEach(key => {
    //initialize object with all keys available in data
    sum[key] = { history: 0, forecast: 0 }
  })

  data.forEach(data => {
    return Object.keys(data).forEach(key => {
      const originalKey = key.split('-')[0] as TraceKey
      const currentSuffix = key.split('-')[1]
      const customTrace = customLayout?.traces.find(trace => trace.key === originalKey) ?? traceConf[originalKey]?.custom
      const suffixColorIndex = suffixes.findIndex(suffix => suffix.name === currentSuffix)

      if (sum[key]) {
        sum[key].customColor = customTrace?.suffixColors?.[suffixColorIndex]?.color
      }

      if (customTrace?.sign === -1 && sum[key]?.history) {
        return (sum[key].history! -= getRoundedNumber((data[key] as number) / factor))
      } else {
        if (sum[key]?.history !== undefined && (data[key] as number) >= 0) {
          return (sum[key].history! += getRoundedNumber((data[key] as number) / factor))
        }
      }
    })
  })

  return sum
}

const SystemChartComparison: React.FC<Props> = ({
  storageId,
  view,
  customLayout,
  handleSaveChart,
  handleDeleteChart,
  from,
  to,
  rechartData,
  maxHeight,
  pieChartTooltipText = '',
  isDynamicPowerPriceEnabled,
  toggleHideLegend,
}) => {
  const [combinedRechartData, setRechartData] = useState<RechartData>(rechartData)
  const prevFrom = useMemo(() => moment(from).subtract(1, 'year'), [from])
  const prevTo = useMemo(() => moment(to).subtract(1, 'year'), [to])
  const currYear = from.year()
  const prevYear = prevFrom.year()

  const prevYearData = useFetchData({ storageId, from: prevFrom, to: prevTo, view })

  const enrichData = (originalData: RechartData, prevData: RechartData, suffixes: TraceSuffix[]): RechartData => {
    const currentYearTransformed = originalData.data?.map(currData => {
      const combinedObj: RechartDataObject = {}
      Object.keys(currData).forEach(key => {
        if (key === 'ts') {
          combinedObj['ts'] = currData[key]
        } else {
          combinedObj[`${key}-${currYear}`] = currData[key]
        }
      })
      return combinedObj
    })

    const prevYearTransformed = prevData.data?.map(pData => {
      const combinedObj: RechartDataObject = {}
      Object.keys(pData).forEach(key => {
        if (key === 'ts') {
          combinedObj['dayMonth'] = moment(pData[key] as string).format('DD.MM')
        } else {
          combinedObj[`${key}-${prevYear}`] = pData[key]
        }
      })
      return combinedObj
    })

    const allCombined = currentYearTransformed?.map(current => {
      const prevValue = prevYearTransformed?.find(d => d['dayMonth'] === moment(current.ts as number).format('DD.MM'))

      return {
        ...current,
        ...prevValue,
      }
    })

    if (allCombined) {
      return {
        ...originalData,
        sums: getSums(allCombined, view === 'day' ? 4 : 1, suffixes, customLayout),
        data: allCombined,
      }
    }

    return { loadingState: { state: 0 } }
  }

  const suffixes = getComparisonYears(from, prevFrom)

  useMemo(() => {
    setRechartData(enrichData(rechartData, prevYearData, suffixes))
  }, [rechartData, prevYearData]) //eslint-disable-line

  return (
    <SystemChart
      view={view}
      to={to}
      from={from}
      showForecast={false} //Comparison should never be affected by forecast settings
      rechartData={combinedRechartData}
      loadingState={combinedRechartData.loadingState}
      customLayout={customLayout}
      handleSaveChart={handleSaveChart}
      handleDeleteChart={handleDeleteChart}
      traceSuffixes={suffixes}
      maxHeight={maxHeight}
      pieChartTooltipText={pieChartTooltipText}
      isDynamicPowerPriceEnabled={isDynamicPowerPriceEnabled}
      toggleHideLegend={toggleHideLegend}
    />
  )
}

export default SystemChartComparison
